import type { MenuItem } from "../lib/types";
import { useLang } from "../lib/useLang";
import { useCart } from "../state/CartContext";
import { useSheet } from "../state/SheetContext";
import { categoryIcon } from "../lib/categoryIcon";
import { dishImage } from "../data/images";
import { Money } from "./Money";
import { QtyStepper } from "./QtyStepper";
import { SmartImage } from "./SmartImage";

export function ItemCard({ item }: { item: MenuItem }) {
  const { t, pick } = useLang();
  const { qtyOf, add, setQty } = useCart();
  const { open } = useSheet();
  const qty = qtyOf(item.id);
  const photo = item.imageURL || dishImage(item.name, item.category);
  const hasOptions = Boolean(item.options?.length);

  const sheetPayload = {
    itemId: item.id,
    type: "standing" as const,
    name: item.name,
    nameAr: item.nameAr,
    price: item.price,
    category: item.category,
    imageURL: photo,
    popular: item.tag === "popular",
  };

  return (
    <div
      className="group flex cursor-pointer items-start gap-3 rounded-lg border-b border-black/5 py-4 transition-colors hover:bg-brand/[0.025]"
      onClick={() => open(sheetPayload)}
    >
      <div className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-black/[.04] text-xl">
        <SmartImage
          src={photo}
          alt={pick(item.name, item.nameAr)}
          className="h-full w-full object-cover transition duration-300 group-hover:scale-110"
          fallback={categoryIcon(item.category)}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium leading-snug text-ink">
            {pick(item.name, item.nameAr)}
          </span>
          {item.tag === "popular" && (
            <span className="chip shrink-0 bg-brand/10 text-brand">
              🔥 {t("common.popular")}
            </span>
          )}
        </div>
        <div className="truncate text-sm text-black/45" dir="auto">
          {pick(item.nameAr, item.name)}
        </div>
        <Money value={item.price} className="price mt-1 inline-block" />
      </div>
      <div className="shrink-0 pt-0.5" onClick={(e) => e.stopPropagation()}>
        {qty > 0 && !hasOptions ? (
          <QtyStepper qty={qty} onChange={(q) => setQty(item.id, q)} />
        ) : (
          <button
            className="add-fab"
            aria-label="add"
            onClick={() =>
              hasOptions
                ? open(sheetPayload)
                : add({
                    itemId: item.id,
                    type: "standing",
                    name: item.name,
                    nameAr: item.nameAr,
                    price: item.price,
                    imageURL: photo,
                  })
            }
          >
            +
          </button>
        )}
      </div>
    </div>
  );
}
